import * as THREE from 'three'

/**
 * How a product's materials are set up — one implementation for both apps.
 *
 * SHARED WITH THE CUSTOMIZER (see shared/sectionFrames/types.ts for the arrangement).
 * Depends on `three` and nothing else.
 *
 * WHY THIS IS SHARED
 * ------------------
 * An operator sets a profile up in the admin portal and has to trust the preview predicts
 * what a shopper sees. Until this module existed, the two apps answered that differently
 * in two ways:
 *
 *   1. Both had a `fixMaterials` that classified every material as glass / cut-out /
 *      opaque. They were the same code — the admin's is a TypeScript port of the
 *      Customizer's — except the opaque branch set `opacity = 1` in one and `0.1` in the
 *      other. Two copies of one algorithm, already drifted by one line.
 *
 *   2. The Customizer ALSO applied `env_param.model_material`: a preset (steel, glass,
 *      acrylic) that REPLACES every mesh's material. The admin portal had none of it. So
 *      a profile using a preset rendered one way for the operator and another for the
 *      shopper, by design rather than by drift — and no amount of keeping the two in sync
 *      would have closed it, because only one of them had the feature.
 *
 * The second is the larger effect by far, and it is invisible on a profile that leaves
 * the material at 'default'.
 */

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export interface MaterialSettings {
  material_metalness: number
  material_roughness: number
  material_transmission: number
  material_thickness: number
  material_ior: number
  material_opacity: number
  material_alphaTest: number
  material_side: 'front' | 'back' | 'double'
}

export const materialPresets: Record<string, MaterialSettings> = {
  default: {
    material_metalness: 0,
    material_roughness: 0.5,
    material_transmission: 0,
    material_thickness: 0,
    material_ior: 1.5,
    material_opacity: 1,
    material_alphaTest: 0,
    material_side: 'front',
  },
  acrylic: {
    material_metalness: 0,
    material_roughness: 0,
    material_transmission: 0.95,
    material_thickness: 0.5,
    material_ior: 1.5,
    material_opacity: 1,
    material_alphaTest: 0,
    material_side: 'double',
  },
  glass: {
    material_metalness: 0,
    material_roughness: 0,
    material_transmission: 0.9,
    material_thickness: 0.3,
    material_ior: 1.52,
    material_opacity: 1,
    material_alphaTest: 0,
    material_side: 'double',
  },
  steel: {
    material_metalness: 1,
    material_roughness: 0.6,
    material_transmission: 0,
    material_thickness: 0,
    material_ior: 1.5,
    material_opacity: 1,
    material_alphaTest: 0.3,
    material_side: 'front',
  },
}

export function getMaterialPreset(name?: string | null): MaterialSettings {
  return materialPresets[name ?? ''] ?? materialPresets.default
}

/**
 * Normalise `env_param.model_material` to its string value.
 *
 * The admin portal stores select values as `{ label, value }` while older profiles carry
 * a plain string. Both guards below compare against `'default'`, so an object silently
 * failed the "keep the GLB material" check and every mesh was handed a brand new material
 * — which replaces `mesh.material`, discarding any engraving overlay on it while leaving
 * the overlay's stored reference pointing at the detached material. The overlay then
 * measures as healthy while rendering nothing.
 */
export function resolveModelMaterial(envParam: any): string {
  const raw = envParam?.model_material
  if (raw && typeof raw === 'object') return raw.value || 'default'
  return raw || 'default'
}

// ---------------------------------------------------------------------------
// Base pass
// ---------------------------------------------------------------------------

const TEXTURE_SLOTS = ['map', 'emissiveMap', 'alphaMap', 'normalMap', 'roughnessMap', 'metalnessMap'] as const

/**
 * Sane texture filtering, and a transparency classification per material.
 *
 * Three cases, because a product GLB mixes them: genuine transparency (a glass lid), a
 * cut-out decal whose alpha is a shape rather than a blend, and everything else. Treating
 * the second as transparent sorts it against the opaques and makes it flicker; treating
 * the first as opaque makes a lid solid.
 */
export function fixProductMaterials(product: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
  renderer.sortObjects = true
  const maxAniso = renderer.capabilities.getMaxAnisotropy()

  product.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh || !mesh.material) return

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]

    materials.forEach((m) => {
      const mat = m as THREE.MeshStandardMaterial

      TEXTURE_SLOTS.forEach((slot) => {
        const tex = mat[slot] as THREE.Texture | null
        if (!tex?.isTexture) return

        tex.anisotropy = maxAniso
        if (slot === 'alphaMap') {
          // A cut-out mask must not be mip-blurred: softened edges make the
          // silhouette wobble as the model shrinks on screen.
          tex.generateMipmaps = false
          tex.minFilter = THREE.LinearFilter
        } else {
          tex.generateMipmaps = true
          tex.minFilter = THREE.LinearMipmapLinearFilter
        }
        tex.magFilter = THREE.LinearFilter
        tex.needsUpdate = true
      })

      const hasAlphaMap = !!mat.alphaMap
      const hasRGBAColor = !!(mat.map && mat.map.format === THREE.RGBAFormat)
      const isGlass =
        (mat.transparent && mat.opacity < 1) ||
        ((mat as THREE.MeshPhysicalMaterial).transmission ?? 0) > 0 ||
        /glass|lid/i.test(mat.name || '')

      if (isGlass) {
        mat.transparent = true
        mat.alphaTest = 0
        mat.depthWrite = false
        mat.depthTest = true
        mesh.renderOrder = Math.max(mesh.renderOrder || 0, 10) // after the opaques
      } else if (hasAlphaMap || hasRGBAColor) {
        // A cut-out: opaque with an alpha test, so it depth-sorts normally.
        mat.transparent = false
        mat.alphaTest = Math.max(mat.alphaTest || 0, 0.01)
        mat.depthWrite = true
        mat.depthTest = true
        mesh.renderOrder = 0
      } else {
        mat.transparent = false
        // 1, not 0.1. The two apps disagreed here. With `transparent: false` the
        // value is mostly inert, but it is written to the framebuffer's alpha and
        // becomes visible the moment anything downstream turns transparency on —
        // so the opaque case should say what it means.
        mat.opacity = 1
        mat.alphaTest = 0
        mat.depthWrite = true
        mat.depthTest = true
        mesh.renderOrder = 0
      }

      mat.needsUpdate = true
    })
  })
}

// ---------------------------------------------------------------------------
// env_param presets
// ---------------------------------------------------------------------------

function sideFrom(value: string | undefined): THREE.Side {
  if (value === 'back') return THREE.BackSide
  if (value === 'double') return THREE.DoubleSide
  return THREE.FrontSide
}

/**
 * Rebuild one mesh's material from the profile's chosen preset.
 *
 * Destructive by nature — it replaces `mesh.material` — so anything holding a reference to
 * the old one has to be told. The overlay bookkeeping below is why: a section overlay
 * clones the material and stores the clone, and swapping the material underneath leaves
 * that clone orphaned and the overlay rendering nothing while still looking installed.
 */
export function applyMaterialFromEnvParam(mesh: THREE.Mesh, envParam: any): void {
  if (!mesh?.isMesh || !envParam) return

  const materialType = resolveModelMaterial(envParam)
  if (materialType === 'default') {
    // Keep the GLB's own material.
    ;(mesh.material as THREE.Material).transparent = true
    return
  }

  const settings: MaterialSettings = { ...getMaterialPreset(materialType) }
  for (const key of Object.keys(settings) as (keyof MaterialSettings)[]) {
    if (envParam[key] !== undefined) (settings as any)[key] = envParam[key]
  }

  const existing = mesh.material as THREE.MeshStandardMaterial | undefined
  const useTransmission = (settings.material_transmission || 0) > 0
  const MaterialClass = useTransmission ? THREE.MeshPhysicalMaterial : THREE.MeshStandardMaterial

  const props: Record<string, unknown> = {
    color: existing?.color ?? 0xffffff,
    metalness: settings.material_metalness ?? 0,
    roughness: settings.material_roughness ?? 0.5,
    transparent: true,
    opacity: settings.material_opacity ?? 1,
    alphaTest: settings.material_alphaTest ?? 0,
    side: sideFrom(settings.material_side),
  }

  // The preset governs the surface response; the GLB still owns the artwork.
  for (const slot of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'] as const) {
    if (existing?.[slot]) props[slot] = existing[slot]
  }

  if (useTransmission) {
    props.transmission = settings.material_transmission ?? 0
    props.thickness = settings.material_thickness ?? 0
    props.ior = settings.material_ior ?? 1.5
  }

  // Tear down anything attached to the material being replaced.
  const ud = mesh.userData as Record<string, any>
  if (typeof ud.shaderMaterial?.dispose === 'function') {
    ud.shaderMaterial.dispose()
    ud.shaderMaterial = undefined
  }
  if (ud.overlayMesh) {
    mesh.remove(ud.overlayMesh)
    if (typeof ud.overlayMesh.material?.dispose === 'function') ud.overlayMesh.material.dispose()
    ud.overlayMesh = undefined
  }

  if (mesh.material) {
    // Keep the GLB's original once, so a later switch back to 'default' has something
    // to restore; dispose only the materials this function itself created.
    if (!ud.originalBaseMaterial) ud.originalBaseMaterial = mesh.material
    else if (mesh.material !== ud.originalBaseMaterial) (mesh.material as THREE.Material).dispose()
  }

  mesh.material = new MaterialClass(props as any)

  // `clone()` has not always carried transmission reliably, so the flag is recorded
  // rather than re-derived from the material later.
  ud.hasTransmission = useTransmission
  ud.originalMaterial = (mesh.material as THREE.Material).clone()

  if (useTransmission) {
    const stored = ud.originalMaterial as THREE.MeshPhysicalMaterial
    stored.transmission = settings.material_transmission ?? 0
    stored.thickness = settings.material_thickness ?? 0
    stored.ior = settings.material_ior ?? 1.5
  }
}

/**
 * Apply the profile's material preset across a product.
 *
 * Logo meshes are skipped: a logo sheet's material carries its artwork with the alpha that
 * cuts out its silhouette, and handing it a steel preset turns the mark into a solid card.
 */
export function applyProductEnvMaterials(product: THREE.Object3D | null, envParam: any): boolean {
  if (!product || !envParam) return false

  const materialType = resolveModelMaterial(envParam)
  if (!materialType || materialType === 'default') return false

  let applied = false
  product.traverse((child) => {
    const mesh = child as THREE.Mesh
    if (!mesh.isMesh) return
    if (mesh.name?.toLowerCase().includes('logo')) return
    applyMaterialFromEnvParam(mesh, envParam)
    applied = true
  })
  return applied
}
